import { makeStyles } from '@mui/styles'
import React, { useEffect, useMemo } from 'react'

import endpoints from '@app/src/api/endpoints'
import ProviderSelectorWithFilters from '@app/src/components/CreateNewRequestModal/ProviderSelectorWithFilters'
import CreationModalContainer from '@app/src/components/CreationModal/CreationModalContainer'
import ControlledDateField from '@app/src/components/Form/ControlledDateField'
import type { Option } from '@app/src/components/Form/Select'
import Select from '@app/src/components/Form/Select/ControlledSelect'
import { useCreationModalProgress } from '@app/src/context/CreationModalProgressContext'
import { useQuestionnairePeriodOptions } from '@app/src/hooks/questionnairePeriodOptions'
import { usePeriodName } from '@app/src/hooks/usePeriodName'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { SortOrder } from '@app/src/types/filter'
import type { QuestionnaireTemplate, Request } from '@app/src/types/resourceExplorer'
import { ScrapingType } from '@app/src/types/resourceExplorer'
import { Alert } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import type { BaseFormData } from '..'
import { Periods } from '../PeriodSelector/PeriodSelector'
import RequestQuestionStep from '../RequestQuestionStep'
import ReviewTable from '../ReviewTable'
import { calculateStartsAndEndsAtDate } from '../utils'

const useStyles = makeStyles(({ spacing }) => ({
  infoAlert: {
    marginTop: spacing(2),
  },
}))

type WfStandardQuestionnaireProps = {
  onClose: () => void
  onBack: () => void
  disableInitialBack?: boolean
  selectedQuestionnaireTemplates?: QuestionnaireTemplate[] | undefined
}

export type QuestionStepData = {
  [key: string]: string
}

export enum WF_STANDARD_QUESTIONNAIRE_QUESTION_KEYS {
  TEMPLATE_IDS = 'templateIds',
  PERIOD_NAME = 'dateYear',
  RESPONDERS = 'responders',
  DEADLINE = 'deadline',
  REVIEW = 'review',
  ACTIVATE_AT = 'activateAt',
}

export type FormData = BaseFormData

const WfStandardQuestionnaire: React.FC<WfStandardQuestionnaireProps> = ({
  onClose,
  onBack,
  disableInitialBack,
  selectedQuestionnaireTemplates,
}) => {
  const { formatMessage } = useIntl()
  const { activeStep, setTotalSteps } = useCreationModalProgress()
  const { questionnaireYearOptions } = useQuestionnairePeriodOptions()
  const { getPeriodDisplayName } = usePeriodName()
  const classes = useStyles()

  const formMethods = useForm<FormData>({
    shouldUnregister: false,
    defaultValues: {
      templateIds: selectedQuestionnaireTemplates?.map(x => ({ id: x.id, title: x.title })) ?? [],
      responders: [],
      dateType: Periods.Yearly,
      deadline: '',
    },
  })
  const { control, getValues, setValue, watch } = formMethods

  const selectedTemplateIds = (watch(WF_STANDARD_QUESTIONNAIRE_QUESTION_KEYS.TEMPLATE_IDS) ?? []).map(
    template => template.id,
  )
  const { periodStartsAt, periodEndsAt } = calculateStartsAndEndsAtDate({
    dateType: watch('dateType'),
    dateYear: watch(WF_STANDARD_QUESTIONNAIRE_QUESTION_KEYS.PERIOD_NAME),
  })
  const periodName = periodStartsAt ? String(getPeriodDisplayName(periodStartsAt, periodEndsAt)) : null

  const WF_REPORT_QUESTIONS = [
    {
      title: formatMessage({ id: 'form.createRequest.selectTemplateTitle' }),
      description: formatMessage({ id: 'form.createRequest.selectTemplateBody' }),
      fieldnames: [WF_STANDARD_QUESTIONNAIRE_QUESTION_KEYS.TEMPLATE_IDS],
      children: (
        <Select<{ title: string; id: number }, Request>
          name={WF_STANDARD_QUESTIONNAIRE_QUESTION_KEYS.TEMPLATE_IDS}
          fieldLabel={formatMessage({ id: 'form.createRequest.selectTemplateLabel' })}
          control={control}
          required
          enableAutoSelect
          multiple
          forceFetch
          navigation={{
            url: endpoints.worldfavorTemplates,
            type: 'post',
            postObject: {
              include: [],
              filter: [
                {
                  name: 'scrapingType',
                  filters: [{ value: ScrapingType.AdverseScraping, operator: Operators.NotEqualTo }],
                },
              ],
              sort: { target: 'title', order: SortOrder.ASCENDING },
            },
          }}
          findSelectedValue={(value, option): boolean => value?.id === option?.value?.id}
          objectToOption={({ title, id }: Request): Option<{ title: string; id: number }> => ({
            label: title,
            value: { title, id },
          })}
        />
      ),
    },
    {
      title: formatMessage({ id: 'form.createRequest.selectPeriodTitle' }),
      description: formatMessage({ id: 'form.createRequest.selectPeriodBody' }),
      fieldnames: [WF_STANDARD_QUESTIONNAIRE_QUESTION_KEYS.PERIOD_NAME],
      children: (
        <Select
          options={questionnaireYearOptions}
          fieldLabel={formatMessage({ id: 'form.createRequest.selectPeriodLabel' })}
          onInputChange={(e, value) => {
            if (!value) setValue(WF_STANDARD_QUESTIONNAIRE_QUESTION_KEYS.PERIOD_NAME, undefined)
          }}
          defaultValue={-1}
          required
          enableAutoSelect
          name={WF_STANDARD_QUESTIONNAIRE_QUESTION_KEYS.PERIOD_NAME}
          control={control}
        />
      ),
    },
    {
      title: formatMessage({ id: 'form.createRequest.selectCompaniesTitle' }),
      description: formatMessage({ id: 'form.createRequest.selectCompaniesBody' }),
      fieldnames: [WF_STANDARD_QUESTIONNAIRE_QUESTION_KEYS.RESPONDERS],
      children: (
        <ProviderSelectorWithFilters
          control={control}
          setValue={setValue}
          templateIds={selectedTemplateIds}
          periodName={periodName}
        />
      ),
    },
    {
      title: formatMessage({ id: 'form.createRequest.selectDeadlineTitle' }),
      description: formatMessage({ id: 'form.createRequest.selectDeadlineBody' }),
      fieldnames: [WF_STANDARD_QUESTIONNAIRE_QUESTION_KEYS.DEADLINE],
      hasSkipButton: true,
      children: (
        <ControlledDateField
          disablePast
          fieldLabel={formatMessage({ id: 'form.createRequest.selectDeadlineLabel' })}
          control={control}
          name={WF_STANDARD_QUESTIONNAIRE_QUESTION_KEYS.DEADLINE}
          slotProps={{ textField: { fullWidth: true } }}
        />
      ),
    },
    {
      title: formatMessage({ id: 'form.createRequest.reviewTitle' }),
      description: (
        <Alert severity="info" className={classes.infoAlert}>
          {formatMessage({ id: 'form.createRequest.reviewInfo' })}
        </Alert>
      ),
      fieldnames: [],
      children: <ReviewTable data={getValues()} />,
    },
  ]

  const activeQuestion = useMemo(() => WF_REPORT_QUESTIONS[activeStep - 1], [activeStep])

  useEffect(() => {
    setTotalSteps(WF_REPORT_QUESTIONS.length)
  }, [])

  return (
    <CreationModalContainer title={formatMessage({ id: 'form.createRequest.wfReportTitle' })} onClose={onClose}>
      <FormProvider {...formMethods}>
        {activeQuestion && (
          <RequestQuestionStep
            fieldnames={activeQuestion.fieldnames}
            title={activeQuestion.title}
            description={activeQuestion.description}
            key={activeQuestion.title}
            hasSkipButton={activeQuestion.hasSkipButton}
            onClose={onClose}
            onBack={onBack}
            disableInitialBack={disableInitialBack}
          >
            {activeQuestion.children}
          </RequestQuestionStep>
        )}
      </FormProvider>
    </CreationModalContainer>
  )
}

export default WfStandardQuestionnaire
